import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import useReorderable from "../../hooks/useReorderable";

const LABEL_INPUT_CLASS = "flex-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";
const CODE_INPUT_CLASS = "w-32 shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-[11px] uppercase tracking-wide text-gray-700 placeholder:text-gray-300 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";
const CODE_READONLY_CLASS = "w-32 shrink-0 truncate rounded-md bg-gray-50 px-2 py-1.5 text-left font-mono text-[11px] uppercase tracking-wide text-gray-500";

const RenditionOptions = ({
  isCanonical,
  answerType,
  options,
  optionLabels,
  secretaryOptionLabels,
  onReorder,
  onAddOption,
  onRemoveOption,
  onChangeOptionCode,
  onChangeOptionLabel,
  onToggleOptionDisabled,
}) => {
  const optionsLabel = answerType === "TEXT" ? "Suggestions" : "Options";
  const addOptionLabel = answerType === "TEXT" ? "Ajouter une suggestion" : "Ajouter une option";

  const orderedEntries = isCanonical
    ? options.map((opt) => {
        const label = optionLabels?.find((l) => l.optionId?.toString() === opt._id?.toString());
        return { optionId: opt._id, code: opt.code, value: label?.value || "" };
      })
    : optionLabels.map((label) => {
        const canonical = options.find((opt) => opt._id?.toString() === label.optionId?.toString());
        return { optionId: label.optionId, code: canonical?.code || "", value: label.value || "", disabled: !!label.disabled };
      });

  const { getDragProps, isDragging, showDropBefore, showDropAfter } = useReorderable(orderedEntries, (newEntries) => {
    onReorder(newEntries.map((e) => e.optionId));
  });

  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">{optionsLabel}</label>
      {orderedEntries.length === 0 ? (
        <p className="text-xs text-gray-400">Aucune option pour le moment</p>
      ) : (
        <div className="space-y-1.5">
          {orderedEntries.map((entry, index) => {
            const secretaryLabel = secretaryOptionLabels?.find((l) => l.optionId?.toString() === entry.optionId?.toString())?.value;
            return (
              <div key={entry.optionId} {...getDragProps(index)} className={`relative ${isDragging(index) ? "opacity-30" : ""}`}>
                {showDropBefore(index) && <div className="absolute -top-0.5 left-0 right-0 h-0.5 bg-primary-500" />}
                <div className={`flex items-center gap-2 ${!isCanonical && entry.disabled ? "opacity-50" : ""}`}>
                  <MdDragIndicator className="h-4 w-4 shrink-0 cursor-grab text-gray-300 hover:text-gray-500" />
                  {!isCanonical && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                      checked={!entry.disabled}
                      onChange={() => onToggleOptionDisabled(entry.optionId)}
                      title={entry.disabled ? "Réactiver pour cette audience" : "Désactiver pour cette audience"}
                    />
                  )}
                  {isCanonical ? (
                    <input
                      className={CODE_INPUT_CLASS}
                      placeholder="CODE"
                      value={entry.code || ""}
                      onChange={(e) => onChangeOptionCode(entry.optionId, e.target.value)}
                    />
                  ) : (
                    <span className={CODE_READONLY_CLASS} title={entry.code || ""}>{entry.code || "—"}</span>
                  )}
                  <input
                    className={LABEL_INPUT_CLASS}
                    placeholder={isCanonical ? "Libellé" : secretaryLabel || "Libellé"}
                    value={entry.value || ""}
                    onChange={(e) => onChangeOptionLabel(entry.optionId, e.target.value)}
                  />
                  {isCanonical && (
                    <button
                      type="button"
                      className="shrink-0 rounded p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500"
                      onClick={() => onRemoveOption(entry.optionId)}
                      title="Supprimer cette option"
                    >
                      <HiOutlineTrash className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {showDropAfter(index, orderedEntries.length) && <div className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary-500" />}
              </div>
            );
          })}
        </div>
      )}
      {isCanonical && (
        <button
          type="button"
          onClick={onAddOption}
          className="mt-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-primary-600"
        >
          <HiOutlinePlus className="h-3.5 w-3.5" />
          {addOptionLabel}
        </button>
      )}
    </div>
  );
};

export default RenditionOptions;
